/**
 * Typed wrapper for the agent's `/api/backup` + `/api/restore` surface
 * (F5). `download()` fetches the full `ReefAppBackupV1` bundle and hands
 * it back as a parsed object so the caller can serialize it to a Blob and
 * save `reefapp-backup-<ISO>.json`. `restore()` uploads a parsed bundle
 * and reports whether the agent must be restarted to reopen DB handles.
 *
 * Routes covered here:
 *   GET  /api/backup?includeSecrets=0|1 → full bundle (JSON)
 *   POST /api/restore  (body = bundle)  → { ok, restartRequired }
 */
import { agentFetch } from './agent-fetch';
import { loadPublicEnv } from './env';

export type Result<T> = { ok: true; value: T } | { ok: false; error: string };

/** Minimal client-side mirror of the agent's `ReefAppBackupV1`. */
export interface BackupBundle {
  readonly version: 1;
  readonly createdAt: string;
  readonly db: {
    readonly reefappDbBase64: string;
    readonly eventsDbBase64?: string;
  };
  readonly data: Record<string, unknown>;
  readonly runtime: Record<string, unknown>;
  readonly includesSecrets: boolean;
}

function baseUrl(): string {
  return loadPublicEnv().agentDevUrl.replace(/\/+$/, '');
}

function errorFromBody(body: unknown, status: number, statusText: string): string {
  if (
    body &&
    typeof body === 'object' &&
    'error' in body &&
    typeof (body as { error: unknown }).error === 'string'
  ) {
    return (body as { error: string }).error;
  }
  return `HTTP ${status} ${statusText}`;
}

export const backupApi = {
  /**
   * GET /api/backup — fetch the full backup bundle. Pass
   * `includeSecrets` to embed `secrets.json` (credentials) in the file.
   */
  async download(includeSecrets: boolean): Promise<Result<BackupBundle>> {
    const qs = includeSecrets ? '?includeSecrets=1' : '';
    try {
      const res = await agentFetch(`${baseUrl()}/api/backup${qs}`, {
        method: 'GET',
        headers: { 'content-type': 'application/json' },
      });
      if (!res.ok) {
        let body: unknown = null;
        try {
          body = await res.json();
        } catch {
          // ignore parse failure
        }
        return {
          ok: false,
          error: errorFromBody(body, res.status, res.statusText),
        };
      }
      const value = (await res.json()) as BackupBundle;
      return { ok: true, value };
    } catch (err) {
      return {
        ok: false,
        error: err instanceof Error ? err.message : String(err),
      };
    }
  },

  /**
   * POST /api/restore — upload a parsed bundle. Returns
   * `{ restartRequired }`; the agent must be bounced to reopen the DB.
   */
  async restore(
    bundle: unknown,
  ): Promise<Result<{ restartRequired: boolean }>> {
    try {
      const res = await agentFetch(`${baseUrl()}/api/restore`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(bundle),
      });
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        // ignore parse failure
      }
      if (!res.ok) {
        return {
          ok: false,
          error: errorFromBody(body, res.status, res.statusText),
        };
      }
      const restartRequired =
        body &&
        typeof body === 'object' &&
        'restartRequired' in body &&
        typeof (body as { restartRequired: unknown }).restartRequired ===
          'boolean'
          ? (body as { restartRequired: boolean }).restartRequired
          : true;
      return { ok: true, value: { restartRequired } };
    } catch (err) {
      return {
        ok: false,
        error: err instanceof Error ? err.message : String(err),
      };
    }
  },
};
